//-- Copyright (c) 2021 TMT Insights Group, LLC

import { ActionReducerMapBuilder, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { InventoryFileEntity, Order, OrderDetail, OrderPlacementState, TitleEntity, TitleInput, TitleSearchResponse } from './slice_types';
import { Profiles, TitleSearchMode } from '../../__configs/config_types';
import { LoadingStatuses } from '../../constants/constants';
import { parseFns, orderPlacementConfig } from '../selectors/configSelectors';
import { getTitleDetails, setActiveTitle } from './titleSlice';
import axios from '../../_utils/axios';
import { RootState } from '../store';
import { TitleEntityV2 } from '@/redux/slices/titleV2Slice.types';
import { fetchTitleChildrenV2, fetchTitleV2 } from '@/redux/slices/titleV2Slice';
import focusConfig from '@/components/Focus/configs';
import Form from '@rjsf/core';

const initialState: OrderPlacementState = {
  data: {},
  inventorySelections: [],
  versionSelections: [],
  outputVersions: [],
  squashedOutputVersions: {},
  loadingOutputVersions: false,
  orderDetails: {} as OrderDetail,
  searchResults: {
    data: [],
    loading: false
  },
  billableCustomers: {
    data: [],
    loading: false
  },
  step: 0,
  outputVersionOptions: {},
  versionKeys: {},
  profileData: {} as Profiles,
  primaryVersionIds: [],
  newOrderId: null,
  previewFile: null,
  titleInput: {},
  activeVersion: null,
  validationErrors: {},
  status: LoadingStatuses.fulfilled,
  openCancelationDialog: false,
  formIsIncomplete: false,
  formIsDraft: false,
  isResumedDraft: false,
  draftState: {},
  selectedVersionId: null,
  filters: {
    version_name_filter: [],
    file_type_filter: []
  },
  schemaFormRefs: new Map(),
  selectedHierarchy: '',
  toggledTitleSearchMode: 'default'
};

const versionRequests = new Map<number, Promise<unknown>>();

export const getOutputVersionOptions = createAsyncThunk('orderPlacement/getOutputVersionOptions', async (id: number) => {
  return axios.post(`/inventory/titles/${id}/versions/?page_size=999`).then((res) => {
    const parseVersionsFn = parseFns.orderPlacementConfig?.parseVersionsFn;
    let versions: TitleEntity[] = JSON.parse(JSON.stringify(res.data?.data));
    versions.sort((a, b) => {
      return a.rally_id === null ? 1 : b.rally_id === null ? -1 : (a.rally_id as any) - (b.rally_id as any);
    });
    versions = parseVersionsFn ? parseVersionsFn(versions) : versions;
    return versions;
  });
});

export const getSquashedOutputVersionOptions = createAsyncThunk('orderPlacement/getSquashedOutputVersionOptions', async (titleId: number) => {
  if (versionRequests.has(titleId)) {
    return versionRequests.get(titleId);
  }
  const request = axios
    .post(`/inventory/titles/${titleId}/versions/?page_size=999&squash=true`)
    .then((res) => {
      const parseVersionsFn = parseFns.orderPlacementConfig?.parseVersionsFn;
      const parsedVersions = parseVersionsFn ? parseVersionsFn(res.data?.data) : res.data?.data;
      const versions = parsedVersions.map((version: TitleEntity) => version.id);

      return {
        titleId,
        parsedVersions,
        versions
      };
    })
    .finally(() => versionRequests.delete(titleId));

  versionRequests.set(titleId, request);
  return request;
});

export const searchTitles = createAsyncThunk(
  'orderPlacement/searchTitles',
  async (search: { search: string; order_on?: string; direction?: 'asc' | 'desc'; hierarchy?: string, entity_types_filter?: string[] }, { rejectWithValue, signal }) => {
    const isFocus = window.location.pathname.includes('focus');
    const entity_types_filter = isFocus ? focusConfig.orderPlacementConfig!.entityTypes : orderPlacementConfig.entityTypes;
    const body = {
      ...{
        entity_types_filter,
        thumbnails: true
      },
      ...{
        ...search,
        entity_types_filter: search.entity_types_filter || entity_types_filter,
      }
    };
    return axios
      .post<TitleSearchResponse>(`/inventory/titles/search/?page_size=99&page_num=0`, body, { signal })
      .then((res) => res.data.data)
      .catch(() => rejectWithValue(signal));
  }
);

export const getTitleInventory = createAsyncThunk(
  'orderPlacement/getTitleInventory',
  async ({ id, filters }: any, { getState, rejectWithValue, signal }) => {
    const state = getState() as RootState;
    return axios
      .post(`/inventory/titles/${id}/files/?page_size=999`, { ...state.orderPlacement.filters, ...filters }, { signal })
      .then((res) => {
        if (!res.data?.data) throw new Error();
        const parseFilesFn = parseFns.orderPlacementConfig?.parseFilesFn;
        return parseFilesFn ? parseFilesFn(res.data.data) : res.data.data;
      })
      .catch(() => rejectWithValue(signal));
  }
);

export const deleteFile = createAsyncThunk(
  'orderPlacement/deleteTitle',
  async ({ versionId, fileId }: { versionId: TitleEntity['id']; fileId: InventoryFileEntity['id'] }, { rejectWithValue }) => {
    return axios
      .delete(`/inventory/titles/${versionId}/files/${fileId}/`)
      .then((res) => {
        if (!res.data?.data) throw new Error();
        return fileId;
      })
      .catch(() => rejectWithValue(null));
  }
);

export const fetchTitle = createAsyncThunk('orderPlacement/fetchTitle', async (id: number, { rejectWithValue }) => {
  return axios
    .get<{ data: TitleEntity }>(`/inventory/titles/${id}/`)
    .then((res) => {
      if (!res.data?.data) throw new Error();
      return res.data.data;
    })
    .catch(() => rejectWithValue(null));
});

export const sliceConfig = {
  name: 'orderPlacement',
  initialState,
  reducers: {
    addSchemaFormRef: (state: OrderPlacementState, { payload: { key, ref } }: { payload: { key: string; ref: React.RefObject<Form> } }) => {
      state.schemaFormRefs.set(key, ref);
    },
    removeSchemaFormRef: (state: OrderPlacementState, key: string) => {
      state.schemaFormRefs.delete(key);
    },
    acceptVersionsFromTitleSlice: (state: OrderPlacementState, { payload }: { payload: { versions: TitleEntity[]; id: number } }) => {
      if (!payload || !payload.versions || !payload.versions.length || !payload.id) return;
      const versions = payload.versions
        .filter((v: TitleEntity) => v.parent == payload.id)
        .sort((a: TitleEntity, b: TitleEntity) => {
          return a.rally_id === null ? 1 : b.rally_id === null ? -1 : (a.rally_id as any) - (b.rally_id as any);
        });
      state.outputVersionOptions[payload.id] = versions;
      (versions as TitleEntity[]).forEach((version) => {
        state.versionKeys[version.id] = version;
      });
    },
    setNewOrderId: (state: OrderPlacementState, { payload }: { payload: string | null }) => {
      state.newOrderId = payload;
    },
    clearOrderPlacement: (state: OrderPlacementState) => {
      state.data = initialState.data;
      state.inventorySelections = initialState.inventorySelections;
      state.versionSelections = initialState.versionSelections;
      state.outputVersions = initialState.outputVersions;
      state.searchResults = initialState.searchResults;
      state.orderDetails = initialState.orderDetails;
      state.status = initialState.status;
      state.outputVersionOptions = initialState.outputVersionOptions;
      state.versionKeys = initialState.versionKeys;
      state.squashedOutputVersions = initialState.squashedOutputVersions;
      state.validationErrors = initialState.validationErrors;
      state.titleInput = initialState.titleInput;
      state.formIsIncomplete = initialState.formIsIncomplete;
      state.formIsDraft = initialState.formIsDraft;
      state.openCancelationDialog = initialState.openCancelationDialog;
      state.draftState = initialState.draftState;
      state.isResumedDraft = initialState.isResumedDraft;
      state.billableCustomers = initialState.billableCustomers;
      state.timeZonedDate = initialState?.timeZonedDate;
      state.primaryVersionIds = [];
      state.selectedHierarchy = initialState?.selectedHierarchy;
    },
    setStep: (state: OrderPlacementState, { payload }: { payload: number }) => {
      state.step = payload;
      state.status = LoadingStatuses.fulfilled;
    },
    addInventorySelection: (state: OrderPlacementState, { payload }: { payload: InventoryFileEntity }) => {
      state.inventorySelections.push(payload);
    },
    addOrRemoveInventoryUniqueSectionSelection: (
      state: OrderPlacementState,
      { payload }: { payload: { file: InventoryFileEntity; sections: string[] } }
    ) => {
      const { file, sections } = payload;
      const selections = state.inventorySelections;
      if (selections.find((f) => f.id === file.id)) {
        state.inventorySelections = selections.filter((f) => f.id !== file.id);
      } else {
        const diff = sections.includes(file.file_category ?? file.section)
          ? selections.filter((f) => (f.file_category ?? f.section) !== (file.file_category ?? file.section))
          : selections;
        state.inventorySelections = [...diff, file];
      }
    },
    addInventorySelections: (state: OrderPlacementState, { payload }: { payload: InventoryFileEntity[] }) => {
      state.inventorySelections = [...state.inventorySelections, ...payload];
    },
    removeInventorySelection: (state: OrderPlacementState, { payload }: { payload: InventoryFileEntity }) => {
      state.inventorySelections = state.inventorySelections.filter(({ id }: any) => id !== payload.id);
    },
    removeOutputVersionFile: (state: OrderPlacementState, { payload }: { payload: InventoryFileEntity }) => {
      const { groupId, fileId } = payload;
      // remove the file from the outputVersion selections with the matching groupId
      state.outputVersions = state.outputVersions.map((ov) => {
        if (ov.id === groupId) {
          ov.selections = ov.selections.filter(({ id }: any) => id !== fileId);
        }
        return ov;
      });
    },
    resetInventorySelection: (state: OrderPlacementState) => {
      state.inventorySelections = initialState.inventorySelections;
      state.versionSelections = initialState.versionSelections;
    },
    clearInventoryResults: (state: OrderPlacementState) => {
      state.data = initialState.data;
    },
    addOutputVersion: (state: OrderPlacementState, { payload }: { payload: TitleEntity }) => {
      const { primary_version, title_inputs, ...addlFields } = payload;
      state.versionSelections = [];
      state.outputVersions.push({
        ...addlFields,
        id: new Date().getTime(),
        selections: state.inventorySelections,
        primary_version,
        title_inputs
      });
      state.inventorySelections = [];
      state.titleInput = {};
    },
    removeOutputVersion: (state: OrderPlacementState, { payload }: { payload: number }) => {
      state.outputVersions = state.outputVersions.filter(({ id }) => id !== payload);
    },
    updateOutputVersion: (state: OrderPlacementState, { payload }: { payload: TitleEntity }) => {
      state.outputVersions = state.outputVersions.map((ov) => {
        if (ov.id === payload.id) return payload;
        else return ov;
      });
    },
    setPreviewFile: (state: OrderPlacementState, { payload }: { payload: InventoryFileEntity | null }) => {
      state.previewFile = payload;
    },
    setTitleInput: (state: OrderPlacementState, { payload }: { payload: Partial<TitleInput> }) => {
      state.titleInput = payload;
    },
    clearSearchResults: (state: OrderPlacementState) => {
      state.searchResults = initialState.searchResults;
    },
    addVersionKeys: (state: OrderPlacementState, { payload }: { payload: (TitleEntity | TitleEntityV2)[] }) => {
      payload.forEach((title) => {
        if (title && !state.versionKeys[title.id]) {
          state.versionKeys[title.id] = title;
        }
      });
    },
    clearVersionSelections: (state: OrderPlacementState) => {
      state.versionSelections = initialState.versionSelections;
    },
    clearOutputVersions: (state: OrderPlacementState) => {
      state.outputVersions = initialState.outputVersions;
    },
    clearInventorySelections: (state: OrderPlacementState) => {
      state.inventorySelections = initialState.inventorySelections;
    },
    resumeDraft: (state: OrderPlacementState, { payload }: { payload: Order }) => {
      state.data = payload.inventory || initialState.data;
      state.versionKeys = payload.versionKeys || initialState.versionKeys;
      state.outputVersions = payload.outputVersions || initialState.outputVersions;
      state.versionSelections = payload.versionSelections || initialState.versionSelections;
      state.profileData = payload.profileData || initialState.profileData;
      state.inventorySelections = payload.inventorySelections || initialState.inventorySelections;
      state.titleInput = payload.titleInput || initialState.titleInput;
      state.primaryVersionIds = payload.primaryVersionIds || initialState.primaryVersionIds;
      state.step = payload.step || 0;
    },
    setPrimaryVersionIds: (state: OrderPlacementState, { payload }: { payload: number[] }) => {
      state.primaryVersionIds = payload;
    },
    setVersionSelections: (state: OrderPlacementState, { payload }: { payload: TitleEntity | TitleEntityV2 }) => {
      if (state.versionSelections?.find((v) => v.id == payload.id)) {
        state.versionSelections = state.versionSelections.filter((v) => v.id != payload.id);
      } else {
        state.versionSelections.push(payload);
      }
      if (!state.versionKeys[payload.id]) {
        state.versionKeys[payload.id] = payload;
      }
      state.inventorySelections = [];
      state.outputVersions = [];
    },
    removeVersionSelection: (state: OrderPlacementState, { payload }: { payload: number }) => {
      state.versionSelections = state.versionSelections.filter((v) => v.id !== payload);
    },
    addVersionSelection: (state: OrderPlacementState, { payload }: { payload: TitleEntity | TitleEntityV2 }) => {
      if (!state.versionSelections?.find((v) => v.id == payload.id)) {
        state.versionSelections.push(payload);
      }
      if (!state.versionKeys[payload.id]) {
        state.versionKeys[payload.id] = payload;
      }
      state.inventorySelections = [];
      state.outputVersions = [];
    },
    setAllVersionSelectionsByType: (
      state: OrderPlacementState,
      { payload }: { payload: { versions: TitleEntity[]; titlesById: Record<string, TitleEntity | TitleEntityV2>; isSelected: boolean } }
    ) => {
      let ids: any = [];
      let newVersionSelections = [];
      if (payload.isSelected) {
        ids = new Set(payload.versions.map((pv: TitleEntity) => pv.id));
        newVersionSelections = state.versionSelections.filter((vs) => !ids.has(vs.id));
      } else {
        ids = new Set(state.versionSelections.map((vs) => vs.id));
        newVersionSelections = [...state.versionSelections, ...payload.versions.filter((pv) => !ids.has(pv.id))];
      }

      state.versionSelections = newVersionSelections;
      state.inventorySelections = [];
      state.outputVersions = [];

      const newVersionKeys: Record<string, TitleEntity | TitleEntityV2> = {};
      newVersionSelections.forEach((nvs) => {
        if (!state.versionKeys[nvs.id]) {
          newVersionKeys[nvs.id] = nvs;
        }

        if (nvs.lineage.length > 1) {
          const titles = nvs.lineage.map((id) => payload.titlesById[id]);
          titles.forEach((title) => {
            if (title && !state.versionKeys[title.id]) {
              state.versionKeys[title.id] = title;
            }
          });
        }
      });

      if (Object.keys(newVersionKeys).length > 0) {
        state.versionKeys = { ...state.versionKeys, ...newVersionKeys };
      }
    },
    setValidationErrors: (state: OrderPlacementState, { payload }: { payload: any }) => {
      const { jobId, field, value = true } = payload;
      if (!field) {
        delete state.validationErrors[jobId];
      } else {
        state.validationErrors[jobId] = {
          ...state.validationErrors[jobId],
          [field]: value
        };
      }
    },
    clearValidationErrors: (state: OrderPlacementState) => {
      state.validationErrors = initialState.validationErrors;
    },
    setDraftState: (state: OrderPlacementState, { payload }: { payload: OrderDetail }) => {
      state.draftState = payload;
    },
    toggleCancelationDialog: (state: OrderPlacementState, { payload }: { payload: boolean }) => {
      state.openCancelationDialog = payload;
    },
    toggleFormIsIncomplete: (state: OrderPlacementState, { payload }: { payload: boolean }) => {
      state.formIsIncomplete = payload;
    },
    toggleFormIsDraft: (state: OrderPlacementState, { payload }: { payload: boolean }) => {
      state.formIsDraft = payload;
    },
    setDraftResumed: (state: OrderPlacementState, { payload }: { payload: boolean }) => {
      state.isResumedDraft = payload;
    },
    setSelectedVersionId: (state: OrderPlacementState, { payload }: { payload: number | null }) => {
      state.selectedVersionId = payload;
    },
    setSearchValue: (state: OrderPlacementState, { payload }: { payload: string }) => {
      state.searchValue = payload;
    },
    setFilters(state: OrderPlacementState, { payload }: { payload: any }) {
      state.filters = payload;
    },
    resetFilters(state: OrderPlacementState) {
      state.filters = initialState.filters;
    },
    deleteFileById: (state: OrderPlacementState, { payload }: { payload: number }) => {
      state.data = state.data.filter((file: InventoryFileEntity) => file.id !== payload);
    },
    setSelectedHierarchy: (state: OrderPlacementState, { payload }: { payload: string }) => {
      state.selectedHierarchy = payload;
    },
    setToggledTitleSearchMode: (state: OrderPlacementState, { payload }: { payload: TitleSearchMode }) => {
      state.toggledTitleSearchMode = payload;
    }
  },
  extraReducers: (builder: ActionReducerMapBuilder<OrderPlacementState>) => {
    builder
      .addCase(searchTitles.pending, (state: OrderPlacementState) => {
        state.searchResults.loading = true;
      })
      .addCase(searchTitles.rejected, (state: OrderPlacementState, { meta }) => {
        if (!meta.aborted) {
          state.searchResults.loading = false;
        }
      })
      .addCase(searchTitles.fulfilled, (state: OrderPlacementState, { payload, meta }) => {
        if (meta.arg.search) {
          state.searchValue = meta.arg.search;
        }
        const parseTitlesFn = parseFns.orderPlacementConfig?.parseTitlesFn;
        state.status = LoadingStatuses.fulfilled;
        state.searchResults.data = parseTitlesFn ? parseTitlesFn(payload) : payload;
        state.searchResults.loading = false;
      })
      .addCase(getOutputVersionOptions.pending, (state: OrderPlacementState) => {
        state.loadingOutputVersions = true;
      })
      .addCase(getOutputVersionOptions.rejected, (state: OrderPlacementState) => {
        state.loadingOutputVersions = false;
      })
      .addCase(getOutputVersionOptions.fulfilled, (state: OrderPlacementState, { payload: versions }) => {
        const parentId = versions[0]?.parent;
        if (parentId) {
          state.outputVersionOptions[parentId] = versions;
        }
        versions.forEach((version) => {
          state.versionKeys[version.id] = version;
        });
        state.loadingOutputVersions = false;
      })
      .addCase(getSquashedOutputVersionOptions.pending, (state: OrderPlacementState) => {
        state.loadingOutputVersions = true;
      })
      .addCase(getSquashedOutputVersionOptions.rejected, (state: OrderPlacementState) => {
        state.loadingOutputVersions = false;
      })
      .addCase(getSquashedOutputVersionOptions.fulfilled, (state: OrderPlacementState, { payload }) => {
        const { parsedVersions, versions, titleId } = payload;

        state.squashedOutputVersions[titleId] = versions;
        parsedVersions.forEach((version: TitleEntity) => {
          state.versionKeys[version.id] = version;
        });
        state.loadingOutputVersions = false;
      })
      .addCase(getTitleInventory.pending, (state: OrderPlacementState) => {
        state.status = LoadingStatuses.pending;
      })
      .addCase(getTitleInventory.rejected, (state: OrderPlacementState, { meta }) => {
        state.data = {};
        if (!meta.aborted) {
          state.status = LoadingStatuses.rejected;
        }
      })
      .addCase(getTitleInventory.fulfilled, (state: OrderPlacementState, { payload }) => {
        const files = payload.map((file: InventoryFileEntity) => ({
          ...file,
          file_type: file.file_type?.replace('.', '').toLowerCase(),
          media_info: {
            parsed_audio: file.media_info?.parsed_audio,
            parsed_video: file.media_info?.parsed_video,
            parsed_image: file.media_info?.parsed_image,
            image: file.media_info?.image,
            ...(file.media_info?.MediaInfo ? { MediaInfo: file.media_info.MediaInfo } : {})
          }
        }));
        state.data = files;
        state.status = LoadingStatuses.fulfilled;
      })
      .addCase(setActiveTitle, (state: OrderPlacementState) => {
        state.searchResults = initialState.searchResults;
        state.data = initialState.data;
        state.status = initialState.status;
      })
      .addCase(getTitleDetails.fulfilled, (state: OrderPlacementState, { payload: { titleData } }) => {
        state.versionKeys[titleData.id] = titleData;
      })
      .addCase(fetchTitleChildrenV2.fulfilled, (state: OrderPlacementState, { payload }) => {
        for (const titleData of payload.children) {
          state.versionKeys[titleData.id] = titleData;
        }
      })
      .addCase(fetchTitle.fulfilled, (state: OrderPlacementState, { payload }) => {
        // this may seem like it's doing the same as above
        // but above we are adding a case for the getTitleDetails action from a diff slice
        // which also make addl calls -- here we simply want an individual title for the versionKeys
        state.versionKeys[payload.id] = payload;
      })
      .addCase(fetchTitleV2.fulfilled, (state: OrderPlacementState, { payload }) => {
        state.versionKeys[payload.id] = payload;
      })
      .addCase(deleteFile.fulfilled, (state: OrderPlacementState, { payload: fileId }) => {
        const newFiles = state.data.filter((file: InventoryFileEntity) => file.id !== fileId);
        state.data = newFiles;
      });
  }
};

const orderPlacementSlice = createSlice(sliceConfig);

export const {
  acceptVersionsFromTitleSlice,
  addSchemaFormRef,
  removeSchemaFormRef,
  addInventorySelection,
  addOrRemoveInventoryUniqueSectionSelection,
  addInventorySelections,
  removeInventorySelection,
  removeOutputVersionFile,
  resetInventorySelection,
  addOutputVersion,
  removeOutputVersion,
  updateOutputVersion,
  setStep,
  setNewOrderId,
  clearInventoryResults,
  setPreviewFile,
  setTitleInput,
  clearSearchResults,
  addVersionKeys,
  setVersionSelections,
  removeVersionSelection,
  addVersionSelection,
  setAllVersionSelectionsByType,
  clearVersionSelections,
  clearOutputVersions,
  clearInventorySelections,
  resumeDraft,
  setValidationErrors,
  clearValidationErrors,
  clearOrderPlacement,
  toggleCancelationDialog,
  toggleFormIsIncomplete,
  toggleFormIsDraft,
  setDraftState,
  setDraftResumed,
  setSelectedVersionId,
  setSearchValue,
  setFilters,
  resetFilters,
  deleteFileById,
  setSelectedHierarchy,
  setPrimaryVersionIds,
  setToggledTitleSearchMode
} = orderPlacementSlice.actions;

export default orderPlacementSlice.reducer;
